import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Printer, RefreshCw, Search, SlidersHorizontal, X, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import { getPrintHeaderHtml } from '../../services/printHeaderTemplateService';
import {
  FETCH_CHUNK,
  REPORT_TABLE_HEADER_BG,
  REPORT_TABLE_HEADER_TEXT,
  REPORT_TABLE_HEADER_BORDER,
  applyExcelHeaderRowStyle,
  printHtmlInSameTab,
} from './reportPrintHelpers';
import ClientListGrid, { useClientListGrid } from '../ui/ClientListGrid';

function todayInput() {
  return new Date().toISOString().slice(0, 10);
}

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'Processing', label: 'Processing' },
  { value: 'Return Done', label: 'Return done' },
  { value: 'Return Return', label: 'Return return' },
];

function defaultFilters() {
  return {
    dateFrom: todayInput(),
    dateTo: todayInput(),
    keyword: '',
    status: '',
    employeeId: '',
    clientIds: [],
    clientLabels: {},
  };
}

function normalizeIdArray(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((id) => Number(id)).filter((n) => !Number.isNaN(n));
}

/** Server filters on `ClientReturn.Date`; keyword matches return ref or batch ref exactly. */
function buildPayload(skip, take, f) {
  const d0 = new Date(f.dateFrom + 'T12:00:00');
  const d1 = new Date(f.dateTo + 'T12:00:00');
  const empRaw = f.employeeId === '' || f.employeeId == null ? 0 : Number(f.employeeId);
  const employeeId = Number.isNaN(empRaw) ? 0 : empRaw;
  return {
    Skip: skip,
    Take: take,
    FromYear: d0.getFullYear(),
    FromMonth: d0.getMonth() + 1,
    FromDay: d0.getDate(),
    ToYear: d1.getFullYear(),
    ToMonth: d1.getMonth() + 1,
    ToDay: d1.getDate(),
    Keyword: (f.keyword || '').trim(),
    Status: (f.status || '').trim(),
    EmployeeId: employeeId,
    ClientIds: normalizeIdArray(f.clientIds),
    ClientId: 0,
    IsPickupDate: false,
    IsCreatedDate: false,
    VoucherNo: '',
    BillNo: '',
    StatusIds: [],
    AreaIds: [],
    EmployeeIds: [],
    AgentIds: [],
    CustomerIds: [],
    ExpenseIds: [],
    IsPaid: false,
    IsNotPaid: false,
    IsByDelPickup: false,
  };
}

function formatDate(d) {
  if (d == null || d === '') return '—';
  if (typeof d === 'string') {
    const dt = new Date(d);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  }
  if (typeof d === 'object' && d.Day != null && d.Month != null && d.Year != null) {
    const dt = new Date(d.Year, d.Month - 1, d.Day);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  }
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function ordersDisplay(n) {
  if (n == null || n === '') return '—';
  const v = typeof n === 'number' ? n : parseFloat(n);
  if (Number.isNaN(v)) return '—';
  return Number.isInteger(v) ? String(v) : String(v);
}

function parseNumberField(v) {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
}

function rowToFlat(r, i) {
  const client = r.Client ?? r.client;
  const cr = r.ClientReturn ?? r.clientReturn;
  return {
    '#': i + 1,
    Client: client?.Name ?? client?.name ?? '',
    TotalOrders: ordersDisplay(r.TotalOrders ?? r.totalOrders),
    RefNo: r.RefNo ?? r.refNo ?? '',
    ReturnRefNo: cr?.RefNo ?? cr?.refNo ?? '',
    ReturnDate: formatDate(cr?.Date ?? cr?.date),
    Employee: cr?.EmployeeName ?? cr?.employeeName ?? '',
    Status: r.Status ?? r.status ?? '',
    ReturnStatus: cr?.Status ?? cr?.status ?? '',
  };
}

function rowMatchesTableSearch(r, q) {
  if (!q) return true;
  const o = rowToFlat(r, 0);
  const { '#': _n, ...rest } = o;
  const hay = Object.values(rest)
    .map((x) => String(x ?? '').toLowerCase())
    .join(' ');
  return hay.includes(q);
}

const PRINT_COLUMN_DEFS = [
  { key: '#', label: '#' },
  { key: 'Client', label: 'Client' },
  { key: 'TotalOrders', label: 'Total orders' },
  { key: 'RefNo', label: 'Ref no' },
  { key: 'ReturnRefNo', label: 'Return ref' },
  { key: 'ReturnDate', label: 'Return date' },
  { key: 'Employee', label: 'Employee' },
  { key: 'Status', label: 'Status' },
  { key: 'ReturnStatus', label: 'Return status' },
];

const COL_COUNT = PRINT_COLUMN_DEFS.length;
const TABLE_COL_COUNT = COL_COUNT + 1;

const RIGHT_ALIGN_KEYS = new Set(['TotalOrders']);

const GRID_COLUMNS = [
  ...PRINT_COLUMN_DEFS.filter((c) => c.key !== '#').map((c) => ({
    field: c.key,
    title: c.label,
    hideable: true,
    align: RIGHT_ALIGN_KEYS.has(c.key) ? 'right' : 'left',
  })),
  { field: 'actions', title: 'Print', hideable: true, sortable: false, filterable: false, groupable: false, align: 'center' },
];

function gridCellValue(row, field) {
  if (field === 'actions') return '';
  const o = rowToFlat(row, 0);
  const raw = o[field];
  if (raw == null || raw === '') return '—';
  return String(raw);
}

function computeOrderSum(rows) {
  let totalOrders = 0;
  for (const r of rows) {
    totalOrders += parseNumberField(r.TotalOrders ?? r.totalOrders);
  }
  return { totalOrders };
}

function defaultPrintColumnSelection() {
  return Object.fromEntries(PRINT_COLUMN_DEFS.map((d) => [d.key, true]));
}

function filterSummary(f) {
  const parts = [`${f.dateFrom} → ${f.dateTo}`, 'return date'];
  if ((f.keyword || '').trim()) parts.push(`ref "${(f.keyword || '').trim()}"`);
  if (f.status) parts.push(`status: ${f.status}`);
  if (f.employeeId) parts.push(`employee #${f.employeeId}`);
  if (f.clientIds?.length) parts.push(`${f.clientIds.length} client(s)`);
  return parts.join(' · ');
}

function tdClass(key) {
  if (key === '#') return 'px-2 py-2 tabular-nums text-gray-600';
  if (RIGHT_ALIGN_KEYS.has(key)) return 'px-2 py-2 text-right tabular-nums';
  if (key === 'Client' || key === 'Status' || key === 'ReturnStatus') {
    return 'max-w-[200px] px-2 py-2 whitespace-normal break-words';
  }
  return 'whitespace-nowrap px-2 py-2';
}

function formatClientListItem(c) {
  const name = c.Name ?? c.name ?? '';
  const mob = c.MobileNo1 ?? c.mobileNo1 ?? '';
  const parts = [name, mob].filter(Boolean);
  return parts.join(' · ') || `Client ${c.Id ?? c.id}`;
}

export default function ClientReturnReport() {
  const [appliedFilters, setAppliedFilters] = useState(() => defaultFilters());
  const [draft, setDraft] = useState(() => defaultFilters());
  const [filterModalOpen, setFilterModalOpen] = useState(true);

  const [employees, setEmployees] = useState([]);
  const [clientPickerSearch, setClientPickerSearch] = useState('');
  const [clientPickerResults, setClientPickerResults] = useState([]);
  const [clientPickerLoading, setClientPickerLoading] = useState(false);
  const clientSearchSeq = useRef(0);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState(null);
  const [tableSearch, setTableSearch] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);
  const [excelExporting, setExcelExporting] = useState(false);
  const [printPreparing, setPrintPreparing] = useState(false);
  const [printColumnModalOpen, setPrintColumnModalOpen] = useState(false);
  const [printColumnSelection, setPrintColumnSelection] = useState(defaultPrintColumnSelection);
  const [rowPrintLoadingId, setRowPrintLoadingId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await MainService.EmployeesInDropDown();
        if (!cancelled) setEmployees(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
        if (!cancelled) setEmployees([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (filterModalOpen) {
      setDraft({
        ...appliedFilters,
        clientIds: appliedFilters.clientIds || [],
        clientLabels: appliedFilters.clientLabels || {},
      });
      setClientPickerSearch('');
      setClientPickerResults([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterModalOpen]);

  useEffect(() => {
    if (!filterModalOpen) return;
    const q = clientPickerSearch.trim();
    if (q.length < 2) {
      setClientPickerResults([]);
      setClientPickerLoading(false);
      return;
    }
    const seq = ++clientSearchSeq.current;
    const timer = setTimeout(async () => {
      setClientPickerLoading(true);
      try {
        const res = await MainService.ClientsInAutoComplete({
          filter: {
            filters: [{ field: 'Name', operator: 'contains', value: q }],
            logic: 'and',
          },
        });
        if (seq !== clientSearchSeq.current) return;
        const raw = res?.Data ?? res?.data ?? res;
        setClientPickerResults(Array.isArray(raw) ? raw : []);
      } catch (e) {
        console.error(e);
        if (seq === clientSearchSeq.current) setClientPickerResults([]);
      } finally {
        if (seq === clientSearchSeq.current) setClientPickerLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [clientPickerSearch, filterModalOpen]);

  const loadReport = async (f = appliedFilters) => {
    setLoading(true);
    setHasLoaded(false);
    setLoadProgress({ loaded: 0, total: 0 });
    setRows([]);
    setTotal(0);
    setError('');
    try {
      let skip = 0;
      let serverTotal = 0;
      const accumulated = [];
      for (;;) {
        const body = buildPayload(skip, FETCH_CHUNK, f);
        const res = await MainService.ClientReturnReportV2(body);
        const chunk = res?.Data ?? res?.data ?? [];
        const arr = Array.isArray(chunk) ? chunk : [];
        serverTotal = Number(res?.Total ?? res?.total ?? 0) || 0;
        accumulated.push(...arr);
        setRows([...accumulated]);
        setTotal(serverTotal);
        setLoadProgress({ loaded: accumulated.length, total: serverTotal });
        if (arr.length === 0) break;
        skip += arr.length;
        if (skip >= serverTotal) break;
        if (arr.length < FETCH_CHUNK) break;
      }
      setHasLoaded(true);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Failed to load client return report.');
      setRows([]);
      setTotal(0);
      setHasLoaded(false);
    } finally {
      setLoading(false);
      setLoadProgress(null);
    }
  };

  const applyModalFilters = () => {
    setAppliedFilters({ ...draft });
    setFilterModalOpen(false);
    setTableSearch('');
    loadReport({ ...draft });
  };

  const cancelModal = () => {
    setDraft({ ...appliedFilters });
    setFilterModalOpen(false);
  };

  const openFilterModal = () => setFilterModalOpen(true);

  const onToolbarRefresh = () => {
    if (!hasLoaded || loading) return;
    setTableSearch('');
    loadReport(appliedFilters);
  };

  const clearModalFilters = () => {
    setDraft(defaultFilters());
  };

  const searchQ = tableSearch.trim().toLowerCase();
  const filteredRows = useMemo(() => {
    if (!searchQ) return rows;
    return rows.filter((r) => rowMatchesTableSearch(r, searchQ));
  }, [rows, searchQ]);

  const columns = useMemo(() => GRID_COLUMNS, []);
  const grid = useClientListGrid({ columns, rows: filteredRows, getCellValue: gridCellValue });
  const grandSums = useMemo(() => computeOrderSum(grid.processedRows), [grid.processedRows]);
  const grandTotalRow =
    hasLoaded && !loading && grid.processedRows.length > 0 ? (
      <tr className="border-t-2 border-amber-400 bg-amber-100/95 text-amber-950 font-semibold">
        <td colSpan={2} className="px-2 py-2 text-right text-[11px] font-bold uppercase tracking-wide text-amber-900">
          Grand total · {grid.processedRows.length} line{grid.processedRows.length === 1 ? '' : 's'}
        </td>
        <td className="px-2 py-2 text-right tabular-nums text-xs">{ordersDisplay(grandSums.totalOrders)}</td>
        <td className="px-2 py-2 text-gray-400">—</td>
        <td className="px-2 py-2 text-gray-400">—</td>
        <td className="px-2 py-2 text-gray-400">—</td>
        <td className="px-2 py-2 text-gray-400">—</td>
        <td className="px-2 py-2 text-gray-400">—</td>
        <td className="px-2 py-2 text-gray-400">—</td>
        <td className="px-2 py-2" />
      </tr>
    ) : null;

  const onExportExcel = async () => {
    if (!hasLoaded || loading) {
      setError('Wait for the report to finish loading, then export.');
      return;
    }
    setExcelExporting(true);
    setError('');
    try {
      const all = filteredRows;
      if (!all.length) {
        setError('No rows to export.');
        return;
      }
      const sheetData = all.map((r, i) => {
        const o = rowToFlat(r, i);
        const row = {};
        for (const d of PRINT_COLUMN_DEFS) {
          row[d.label] = o[d.key];
        }
        return row;
      });
      const ws = XLSX.utils.json_to_sheet(sheetData);
      applyExcelHeaderRowStyle(ws);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'ClientReturns');
      const fname = `client-return-report-${appliedFilters.dateFrom}-${appliedFilters.dateTo}.xlsx`;
      XLSX.writeFile(wb, fname);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Excel export failed.');
    } finally {
      setExcelExporting(false);
    }
  };

  const openPrintColumnModal = () => {
    if (!hasLoaded || loading) {
      setError('Wait for the report to finish loading, then print.');
      return;
    }
    if (!filteredRows.length) {
      setError('No rows to print.');
      return;
    }
    setError('');
    setPrintColumnSelection(defaultPrintColumnSelection());
    setPrintColumnModalOpen(true);
  };

  const executeClientReturnPrint = async () => {
    const activeCols = PRINT_COLUMN_DEFS.filter((d) => printColumnSelection[d.key]);
    if (!activeCols.length) {
      setError('Select at least one column to print.');
      return;
    }

    setPrintColumnModalOpen(false);
    setPrintPreparing(true);
    setError('');
    try {
      const all = filteredRows;
      if (!all.length) {
        setError('No rows to print.');
        return;
      }

      const esc = (s) =>
        String(s ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');

      let printHeaderHtml = '';
      try {
        printHeaderHtml = (await getPrintHeaderHtml()) || '';
      } catch (e) {
        console.warn('Client return report print: no print header', e);
      }

      const headHtml = `<tr>${activeCols.map((c) => `<th>${esc(c.label)}</th>`).join('')}</tr>`;
      const bodyHtml = all
        .map((r, i) => {
          const o = rowToFlat(r, i);
          const cells = activeCols.map((c) => o[c.key]);
          return `<tr>${cells.map((c) => `<td>${esc(c)}</td>`).join('')}</tr>`;
        })
        .join('');

      const summary = esc(filterSummary(appliedFilters));
      const headerBlock = printHeaderHtml
        ? `<div class="company-print-header print-repeat-header-cell">${printHeaderHtml}</div>`
        : '';
      const thBg = REPORT_TABLE_HEADER_BG;
      const thColor = REPORT_TABLE_HEADER_TEXT;
      const thBorder = REPORT_TABLE_HEADER_BORDER;
      const htmlDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>&#x200b;</title>
        <style>
          html,body{margin:0;padding:0}
          body{font-family:system-ui,sans-serif;font-size:10px;color:#111}
          .report-print-inner{box-sizing:border-box;padding:12px}
          .company-print-header{margin-bottom:14px}
          .company-print-header img{max-height:72px}
          .report-title{font-size:13px;font-weight:700;margin:0 0 8px;color:#111}
          .meta{color:#444;margin-bottom:12px;max-width:100%}
          table{border-collapse:collapse;width:100%;table-layout:auto}
          th,td{border:1px solid ${thBorder};padding:3px 4px;word-wrap:break-word;vertical-align:top}
          th{background:${thBg} !important;color:${thColor};font-weight:700;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          td{background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          @media print{
            @page{size:auto;margin:0}
            html,body{margin:0 !important;padding:0 !important}
            .report-print-inner{padding:14mm 16mm 16mm 16mm !important;box-sizing:border-box;-webkit-box-decoration-break:clone;box-decoration-break:clone}
            thead{display:table-header-group}
            .company-print-header{page-break-inside:avoid}
          }
        </style></head><body>
        <div class="report-print-inner">
        ${headerBlock}
        <p class="report-title">Client return report</p>
        <div class="meta">${summary}</div>
        <p style="margin:0 0 8px;font-size:10px;color:#666">${all.length} row(s) · Σ orders ${esc(String(grandSums.totalOrders))}</p>
        <table><thead>${headHtml}</thead><tbody>${bodyHtml}</tbody></table>
        </div>
        </body></html>`;
      await printHtmlInSameTab(htmlDoc, 'Client return report print');
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Print failed.');
    } finally {
      setPrintPreparing(false);
    }
  };

  const updateDraft = useCallback((patch) => {
    setDraft((d) => ({ ...d, ...patch }));
  }, []);

  const toggleDraftClient = (c) => {
    const id = Number(c.Id ?? c.id);
    if (Number.isNaN(id)) return;
    const label = formatClientListItem(c);
    setDraft((d) => {
      const next = new Set((d.clientIds || []).map(Number));
      const labels = { ...(d.clientLabels || {}) };
      if (next.has(id)) {
        next.delete(id);
        delete labels[String(id)];
      } else {
        next.add(id);
        labels[String(id)] = label;
      }
      return { ...d, clientIds: [...next], clientLabels: labels };
    });
  };

  const removeDraftClientId = (id) => {
    setDraft((d) => {
      const labels = { ...(d.clientLabels || {}) };
      delete labels[String(id)];
      return { ...d, clientIds: (d.clientIds || []).filter((x) => Number(x) !== Number(id)), clientLabels: labels };
    });
  };

  const onRowServerPrint = async (rowId) => {
    if (rowId == null) return;
    setRowPrintLoadingId(rowId);
    setError('');
    try {
      const response = await MainService.PrintIndClientReturn(rowId);
      if (response) {
        window.open(`${getApiBaseUrl()}ReportFiles/${response}`, '_blank', 'noopener,noreferrer');
      } else {
        setError('Print returned no file name.');
      }
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Row print failed.');
    } finally {
      setRowPrintLoadingId(null);
    }
  };

  return (
    <div className="space-y-4 relative">
      {printColumnModalOpen && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="client-return-print-columns-title"
        >
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-200">
              <h3 id="client-return-print-columns-title" className="text-lg font-semibold text-gray-900">
                Columns for print / PDF
              </h3>
              <button
                type="button"
                onClick={() => setPrintColumnModalOpen(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="px-5 pt-3 text-sm text-gray-600">
              Choose which columns appear in the printable document. Company print header is added at the top automatically.
            </p>
            <div className="px-5 py-3 flex flex-wrap gap-2 border-b border-gray-100">
              <button
                type="button"
                className="text-xs font-medium text-emerald-700 hover:underline"
                onClick={() => setPrintColumnSelection(defaultPrintColumnSelection())}
              >
                Select all
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={() =>
                  setPrintColumnSelection(Object.fromEntries(PRINT_COLUMN_DEFS.map((d) => [d.key, false])))
                }
                className="text-xs font-medium text-gray-600 hover:underline"
              >
                Clear all
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-3 space-y-2">
              {PRINT_COLUMN_DEFS.map((col) => (
                <label
                  key={col.key}
                  className="flex items-center gap-3 rounded-lg border border-gray-100 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!!printColumnSelection[col.key]}
                    onChange={() =>
                      setPrintColumnSelection((prev) => ({
                        ...prev,
                        [col.key]: !prev[col.key],
                      }))
                    }
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-800">{col.label}</span>
                </label>
              ))}
            </div>
            <div className="flex flex-wrap justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => setPrintColumnModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void executeClientReturnPrint()}
                disabled={!PRINT_COLUMN_DEFS.some((d) => printColumnSelection[d.key])}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
              >
                <Printer className="w-4 h-4" />
                Print / PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {filterModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="client-return-filter-title"
        >
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-200 bg-white rounded-t-xl">
              <h3 id="client-return-filter-title" className="text-lg font-semibold text-gray-900">
                Client return report filters
              </h3>
              <button
                type="button"
                onClick={cancelModal}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block text-sm">
                  <span className="text-gray-600">From date</span>
                  <input
                    type="date"
                    value={draft.dateFrom}
                    onChange={(e) => updateDraft({ dateFrom: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600">To date</span>
                  <input
                    type="date"
                    value={draft.dateTo}
                    onChange={(e) => updateDraft({ dateTo: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm sm:col-span-2">
                  <span className="text-gray-600">Keyword (exact ref)</span>
                  <input
                    type="text"
                    value={draft.keyword}
                    onChange={(e) => updateDraft({ keyword: e.target.value })}
                    placeholder="Return ref or batch ref…"
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600">Status</span>
                  <select
                    value={draft.status}
                    onChange={(e) => updateDraft({ status: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm bg-white"
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value || '_all'} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600">Employee</span>
                  <select
                    value={draft.employeeId}
                    onChange={(e) => updateDraft({ employeeId: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm bg-white"
                  >
                    <option value="">All employees</option>
                    {employees.map((e) => (
                      <option key={e.Id ?? e.id} value={e.Id ?? e.id}>
                        {e.Name ?? e.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="rounded-lg border border-gray-100 p-3 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="block text-sm font-medium text-gray-700">Clients (optional)</label>
                  <button
                    type="button"
                    className="text-xs text-indigo-600 hover:underline"
                    onClick={() => updateDraft({ clientIds: [], clientLabels: {} })}
                  >
                    Clear all
                  </button>
                </div>
                <div>
                  <label htmlFor="client-return-client-search" className="text-xs text-gray-600">
                    Search clients (min. 2 characters)
                  </label>
                  <div className="relative mt-1">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      id="client-return-client-search"
                      type="search"
                      value={clientPickerSearch}
                      onChange={(e) => setClientPickerSearch(e.target.value)}
                      placeholder="Name…"
                      autoComplete="off"
                      className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-white">
                  {clientPickerLoading && (
                    <p className="px-3 py-6 text-center text-sm text-gray-500">Searching…</p>
                  )}
                  {!clientPickerLoading && clientPickerSearch.trim().length < 2 && (
                    <p className="px-3 py-6 text-center text-sm text-gray-500">Type at least 2 characters to search.</p>
                  )}
                  {!clientPickerLoading &&
                    clientPickerSearch.trim().length >= 2 &&
                    clientPickerResults.length === 0 && (
                      <p className="px-3 py-6 text-center text-sm text-gray-500">No clients match this search.</p>
                    )}
                  {!clientPickerLoading &&
                    clientPickerResults.map((c) => {
                      const id = Number(c.Id ?? c.id);
                      const checked = draft.clientIds.map(Number).includes(id);
                      return (
                        <label
                          key={`cl-${id}`}
                          className="flex cursor-pointer items-start gap-3 border-b border-gray-100 px-3 py-2.5 last:border-b-0 hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleDraftClient(c)}
                            className="mt-0.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <span className="text-sm text-gray-900 leading-snug">{formatClientListItem(c)}</span>
                        </label>
                      );
                    })}
                </div>
                {draft.clientIds.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-medium text-gray-700">Selected ({draft.clientIds.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {draft.clientIds.map((id) => {
                        const sid = String(id);
                        const lab = (draft.clientLabels && draft.clientLabels[sid]) || `Client #${id}`;
                        return (
                          <span
                            key={sid}
                            className="inline-flex max-w-full items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 pl-2.5 pr-1 py-0.5 text-xs text-emerald-900"
                          >
                            <span className="truncate" title={lab}>
                              {lab}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeDraftClientId(id)}
                              className="shrink-0 rounded-full p-0.5 text-emerald-700 hover:bg-emerald-200/80"
                              aria-label={`Remove ${lab}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="sticky bottom-0 flex flex-wrap justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                type="button"
                onClick={clearModalFilters}
                className="px-4 py-2 rounded-lg border border-amber-200 bg-amber-50 text-sm font-medium text-amber-900 hover:bg-amber-100"
              >
                Clear all filters
              </button>
              <button
                type="button"
                onClick={cancelModal}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyModalFilters}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                <Search className="w-4 h-4" />
                Apply filters
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <h2 className="page-title text-2xl font-semibold text-gray-900">Client Return Report</h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={openFilterModal}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
          </button>
          <button
            type="button"
            onClick={onToolbarRefresh}
            disabled={loading || !hasLoaded}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-40"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={onExportExcel}
            disabled={excelExporting || !hasLoaded || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-sm font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
          >
            <FileSpreadsheet className="w-4 h-4" />
            {excelExporting ? 'Exporting…' : 'Excel'}
          </button>
          <button
            type="button"
            onClick={openPrintColumnModal}
            disabled={printPreparing || !hasLoaded || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
            title="Choose columns, then print or Save as PDF"
          >
            <Printer className="w-4 h-4" />
            {printPreparing ? 'Preparing…' : 'Print / PDF'}
          </button>
        </div>
      </div>

      {hasLoaded && (
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          Active filters: {filterSummary(appliedFilters)}
        </p>
      )}

      {(hasLoaded || loading) && (
        <div className="relative max-w-xl">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
            disabled={loading && rows.length === 0}
            placeholder="Search loaded rows…"
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50 disabled:text-gray-400"
            aria-label="Search in table"
          />
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-2 text-sm">{error}</div>
      )}

      {!hasLoaded && !loading && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white px-4 py-10 text-center text-gray-600 text-sm">
          Use <strong>Apply filters</strong> in the dialog to load the report (dialog opens automatically when you open this
          page).
        </div>
      )}

      <div className="space-y-2">
        {loading && loadProgress != null && (
          <div className="rounded-lg border border-gray-200 bg-white px-3 pb-2 pt-3 shadow-sm">
            <div className="mb-1.5 flex justify-between text-xs text-gray-600">
              <span className="font-medium text-gray-800">Loading…</span>
              <span className="tabular-nums text-gray-700">
                {loadProgress.total > 0
                  ? `${loadProgress.loaded} / ${loadProgress.total} rows`
                  : rows.length > 0
                    ? `${rows.length} rows…`
                    : 'Connecting…'}
              </span>
            </div>
            {loadProgress.total > 0 ? (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-[width] duration-300 ease-out"
                  style={{
                    width: `${Math.min(100, (loadProgress.loaded / Math.max(loadProgress.total, 1)) * 100)}%`,
                  }}
                />
              </div>
            ) : (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div className="h-full w-2/5 animate-pulse rounded-full bg-emerald-400" />
              </div>
            )}
          </div>
        )}
        <ClientListGrid
          columns={columns}
          grid={grid}
          loading={loading && rows.length === 0}
          emptyText={rows.length === 0 ? 'No rows for this filter.' : 'No rows match your search.'}
          minWidth="1100px"
          compact
          tableClassName="w-full text-left text-xs"
          theadClassName="sticky top-0 z-10 border-b border-emerald-300 bg-emerald-100"
          maxHeight="75vh"
          footerExtra={grandTotalRow}
          renderCell={(row, column) => {
            if (column.field === 'actions') {
              const rowId = row.Id ?? row.id;
              return (
                <button
                  type="button"
                  onClick={() => void onRowServerPrint(rowId)}
                  disabled={rowPrintLoadingId === rowId}
                  className="inline-flex items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-[11px] font-medium text-sky-900 hover:bg-sky-100 disabled:opacity-50"
                  title="Server PDF"
                >
                  <Printer className="h-3.5 w-3.5 shrink-0" />
                  {rowPrintLoadingId === rowId ? '…' : 'PDF'}
                </button>
              );
            }
            return row[column.field];
          }}
        />
      </div>
    </div>
  );
}
